import React, { useState, useEffect } from 'react';
import { Avatar, AvatarProps, Tooltip } from '@mui/material';
import md5 from 'md5';

interface GravatarAvatarProps extends Omit<AvatarProps, 'src'> {
  email?: string | null;
  name: string;
  size?: number;
  showTooltip?: boolean;
}

/**
 * Generate a consistent color from a string (name or email)
 */
const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 65%, 45%)`;
};

/**
 * Get initials from name (first letter of first and last word)
 */
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Build Gravatar URL from email
 */
const getGravatarUrl = (email: string, size: number): string => {
  const hash = md5(email.toLowerCase().trim());
  // Use d=404 to get 404 response when no Gravatar exists
  return `https://www.gravatar.com/avatar/${hash}?s=${size * 2}&d=404`;
};

/**
 * Avatar component that uses Gravatar when available, falls back to initials
 */
export const GravatarAvatar: React.FC<GravatarAvatarProps> = ({
  email,
  name,
  size = 32,
  showTooltip = true,
  sx,
  ...props
}) => {
  const [gravatarLoaded, setGravatarLoaded] = useState<boolean | null>(null);

  const initials = getInitials(name);
  const bgColor = stringToColor(email || name);
  const gravatarUrl = email ? getGravatarUrl(email, size) : null;

  // Pre-load the Gravatar image to check if it exists
  useEffect(() => {
    if (!gravatarUrl) {
      setGravatarLoaded(false);
      return;
    }

    // Reset state when URL changes
    setGravatarLoaded(null);

    const img = new Image();
    img.onload = () => {
      setGravatarLoaded(true);
    };
    img.onerror = () => {
      setGravatarLoaded(false);
    };
    img.src = gravatarUrl;
  }, [gravatarUrl]);

  // Show Gravatar if loaded successfully, otherwise show initials with colored background
  const showGravatar = gravatarLoaded === true && gravatarUrl;

  const avatar = (
    <Avatar
      src={showGravatar ? gravatarUrl : undefined}
      imgProps={{
        referrerPolicy: 'no-referrer',
      }}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        fontWeight: 600,
        bgcolor: showGravatar ? 'transparent' : bgColor,
        ...sx,
      }}
      {...props}
    >
      {initials}
    </Avatar>
  );

  if (showTooltip) {
    return <Tooltip title={name}>{avatar}</Tooltip>;
  }

  return avatar;
};

export default GravatarAvatar;
